import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import BeatLoader from "react-spinners/BeatLoader";
import {
  adjustMerchantLedger,
  fetchMerchantLedgerBalances,
} from "../../services/users";
import { fetchAvailableCurrencies } from "../../services/currencies";

const FALLBACK_CURRENCIES = ["EUR", "XAF", "RWF", "KES", "UGX"];

const emptyForm = () => ({
  operation: "CREDIT",
  amount: "",
  currency: "",
  reason: "",
  reference: `adj-${Date.now()}`,
});

const formatAmount = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const MerchantLedgerSection = ({ userId, authToken, onAdjusted }) => {
  const [balances, setBalances] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadBalances = (token = authToken) => {
    if (!userId || !token) return;
    setLoadingBalances(true);
    fetchMerchantLedgerBalances(userId, token)
      .then((res) => {
        if (res.status_code === 200) {
          setBalances(res.data?.balances || []);
        } else {
          setBalances([]);
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
        setBalances([]);
      })
      .finally(() => {
        setLoadingBalances(false);
      });
  };

  useEffect(() => {
    if (!userId || !authToken) return;
    loadBalances(authToken);
    fetchAvailableCurrencies(authToken)
      .then((res) => {
        if (res.status_code === 200 && Array.isArray(res.data) && res.data.length) {
          const codes = res.data
            .map((item) => item.code || item.name)
            .filter(Boolean);
          setCurrencies(Array.from(new Set(codes)));
        } else {
          setCurrencies(FALLBACK_CURRENCIES);
        }
      })
      .catch(() => {
        setCurrencies(FALLBACK_CURRENCIES);
      });
  }, [userId, authToken]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (saving || !userId || !authToken) return;

    const amount = Number(form.amount);
    const currency = form.currency.trim().toUpperCase();
    const reason = form.reason.trim();
    const reference = form.reference.trim();

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    if (!currency) {
      toast.error("Select a currency");
      return;
    }
    if (reason.length < 3) {
      toast.error("Reason must be at least 3 characters");
      return;
    }
    if (reference.length < 3) {
      toast.error("Reference must be at least 3 characters");
      return;
    }
    if (
      form.operation === "DEBIT" &&
      !window.confirm(
        `Debit ${formatAmount(amount)} ${currency} from this merchant?`
      )
    ) {
      return;
    }

    setSaving(true);
    adjustMerchantLedger(
      userId,
      {
        operation: form.operation,
        amount,
        currency,
        reason,
        reference,
      },
      authToken
    )
      .then((res) => {
        if (res.status_code === 200) {
          toast.success(`${res.message}`);
          setLastResult(res.data);
          setForm(emptyForm());
          loadBalances(authToken);
          if (onAdjusted) onAdjusted();
        } else {
          toast.error(`${res.message}`);
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
        toast.error("Failed to adjust merchant ledger");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const isDebit = form.operation === "DEBIT";

  return (
    <div className="py-2 bg-[#fff] rounded-[8px] p-3">
      <p className="font-semibold">Debit / Credit</p>
      <p className="text-sm text-[#767676] mt-1 mb-4">
        Manually adjust this merchant&apos;s ledger balance. Debits require
        sufficient funds in the selected currency.
      </p>

      <div className="mb-5">
        <p className="text-sm font-medium mb-2">Ledger balances</p>
        {loadingBalances ? (
          <BeatLoader
            color="#36d7b7"
            loading={loadingBalances}
            cssOverride={{ textAlign: "left", marginTop: "8px" }}
          />
        ) : balances.length === 0 ? (
          <p className="text-sm text-[#919aa3]">No ledger entries yet.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {balances.map((row) => (
              <div
                key={row.code || row.name}
                className="border border-[#C9D9E9] rounded-[6px] px-3 py-2 min-w-[120px]"
              >
                <p className="text-xs text-[#767676]">{row.code || row.name}</p>
                <p className="font-semibold">{formatAmount(row.amount)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="ledger-operation" className="block text-sm mb-1">
              Operation
            </label>
            <select
              id="ledger-operation"
              className="w-full border border-[#C9D9E9] rounded-[4px] px-3 py-2 bg-[#F2F8FB]"
              value={form.operation}
              onChange={(e) => updateField("operation", e.target.value)}
              disabled={saving}
            >
              <option value="CREDIT">CREDIT</option>
              <option value="DEBIT">DEBIT</option>
            </select>
          </div>
          <div>
            <label htmlFor="ledger-currency" className="block text-sm mb-1">
              Currency
            </label>
            <select
              id="ledger-currency"
              className="w-full border border-[#C9D9E9] rounded-[4px] px-3 py-2 bg-[#F2F8FB]"
              value={form.currency}
              onChange={(e) => updateField("currency", e.target.value)}
              disabled={saving}
            >
              <option value="">Select a currency</option>
              {currencies.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ledger-amount" className="block text-sm mb-1">
              Amount
            </label>
            <input
              id="ledger-amount"
              type="number"
              min="0.01"
              step="0.01"
              className="w-full border border-[#C9D9E9] rounded-[4px] px-3 py-2 bg-[#F2F8FB]"
              value={form.amount}
              onChange={(e) => updateField("amount", e.target.value)}
              disabled={saving}
            />
          </div>
          <div>
            <label htmlFor="ledger-reference" className="block text-sm mb-1">
              Reference
            </label>
            <input
              id="ledger-reference"
              className="w-full border border-[#C9D9E9] rounded-[4px] px-3 py-2 bg-[#F2F8FB]"
              value={form.reference}
              onChange={(e) => updateField("reference", e.target.value)}
              disabled={saving}
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="ledger-reason" className="block text-sm mb-1">
              Reason
            </label>
            <input
              id="ledger-reason"
              className="w-full border border-[#C9D9E9] rounded-[4px] px-3 py-2 bg-[#F2F8FB]"
              value={form.reason}
              onChange={(e) => updateField("reason", e.target.value)}
              disabled={saving}
              placeholder="Audit reason for this adjustment"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className={`${
              isDebit ? "bg-[#E65B65]" : "bg-[#405B9C]"
            } text-[#fff] rounded-[5px] px-4 py-2 text-sm disabled:opacity-60`}
          >
            {saving ? "Submitting..." : isDebit ? "Debit ledger" : "Credit ledger"}
          </button>
          <BeatLoader
            color="#36d7b7"
            loading={saving}
            cssOverride={{ marginTop: "0" }}
          />
        </div>
      </form>

      {lastResult && (
        <p className="text-sm text-[#0F5D5D] mt-4">
          Last {lastResult.operation}: {formatAmount(lastResult.amount)}{" "}
          {lastResult.currency}. New {lastResult.currency} balance:{" "}
          {formatAmount(lastResult.balance)}
          {lastResult.replayed ? " (replayed existing reference)" : ""}.
        </p>
      )}
    </div>
  );
};

export default MerchantLedgerSection;
